import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API_BASE } from "../config";
import {
  RiMoneyDollarCircleLine,
  RiErrorWarningLine,
  RiCheckboxCircleFill,
  RiVerifiedBadgeLine,
  RiPriceTag3Line,
  RiArrowRightLine,
  RiRefreshLine,
  RiFileTextLine,
  RiHistoryLine,
} from "react-icons/ri";
import { Link } from "react-router-dom";

// ─── THEME COLORS (unchanged) ─────────────────────────────
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

/* ─── CSS (unchanged) ─────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}
  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}
  .t-table-wrapper{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};overflow:hidden;box-shadow:${T.shadow}}
  .t-table{width:100%;border-collapse:collapse}
  .t-table th{background:${T.softTeal};padding:16px 20px;text-align:left;font-weight:700;color:${T.navy};border-bottom:1px solid ${T.borderLight}}
  .t-table td{padding:14px 20px;border-bottom:1px solid ${T.borderLight};color:${T.textMid}}
  .t-table tr:last-child td{border-bottom:none}
  .t-table tr:hover td{background:${T.offWhite}}
  .t-price{font-weight:800;font-size:18px;color:${T.primary}}
  .t-price-closed{font-weight:800;font-size:18px;color:#94A3B8;text-decoration:line-through}
  .t-deadline-closed{background:#FEE2E2;color:#EF4444;display:inline-block;margin-left:8px;font-size:10px;padding:2px 6px;border-radius:4px}
  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-4px);box-shadow:${T.shadowLg}}
  .t-card-header{display:flex;align-items:center;gap:12px;margin-bottom:20px}
  .t-card-icon{width:48px;height:48px;border-radius:50%;background:${T.softTeal};display:flex;align-items:center;justify-content:center}
  .t-card-title{font-size:20px;font-weight:800;color:${T.text}}
  .t-cta-w{padding:0 clamp(20px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(32px,4vw,52px) clamp(32px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-r{display:flex;flex-direction:column;align-items:flex-end;gap:10px}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}
  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:72px 24px;gap:12px}
  .t-s-icon{width:52px;height:52px;border-radius:13px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:${T.primary};margin-bottom:4px}
  .t-s-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:14px;color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:9px 20px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}
  @keyframes spin{to{transform:rotate(360deg)}}
  .t-refresh-btn{display:inline-flex;align-items:center;gap:8px;padding:8px 18px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.primary};font-family:'Inter',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;margin-top:16px}
  .t-refresh-btn:hover{background:${T.primary};color:${T.white};border-color:${T.primary}}
  .t-last-updated{font-size:12px;color:${T.textMuted};margin-top:8px}
`;

const RegistrationFeePage = () => {
  const { id: eventId } = useParams();
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Fetch using the same endpoint as admin panel ──
  const fetchRegistrationFee = async (forceRefresh = false) => {
    if (!eventId) {
      setError("No event specified");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the same endpoint as the admin panel: /event/{eventId}
      const cacheBuster = forceRefresh ? `?_t=${Date.now()}` : '';
      const url = `${API_BASE}/api/registration-fee/event/${eventId}${cacheBuster}`;
      console.log("📡 Fetching registration fee from:", url);

      const { data } = await axios.get(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        }
      });

      console.log("📦 Response:", data);

      if (data.success && data.data) {
        let activeData = null;
        // The endpoint returns an array of all versions
        if (Array.isArray(data.data)) {
          // Find the active version
          const active = data.data.find(f => f.isActive === true);
          if (active) {
            activeData = active;
          } else if (data.data.length > 0) {
            // If none is active, pick the first one
            activeData = data.data[0];
          }
        } else {
          // If the API returns a single object (fallback)
          activeData = data.data;
        }

        if (activeData) {
          console.log("✅ Setting feeData:", activeData);
          setFeeData(activeData);
          setLastUpdated(new Date().toLocaleTimeString());
          setError(null);
        } else {
          setError("No registration fee data available for this event");
          setFeeData(null);
        }
      } else {
        setError("No registration fee data available for this event");
        setFeeData(null);
      }
    } catch (err) {
      console.error("❌ Error fetching registration fee:", err);
      if (err.response?.status === 404) {
        setError("Registration fee information not available for this event yet.");
      } else {
        setError("Failed to load registration fee data");
      }
      setFeeData(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Initial fetch ──
  useEffect(() => {
    if (eventId) {
      fetchRegistrationFee();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  const isDeadlinePast = (date) => {
    if (!date) return true;
    return new Date(date) < new Date();
  };

  const formatDate = (date) => {
    if (!date) return "Closed";
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ── Debug log before render ──
  console.log("🔄 Render state:", { loading, error, feeData: feeData ? "present" : "null" });

  // ── Loading State ──
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiMoneyDollarCircleLine size={13} />
                Meetings21 · Registration
              </div>
              <h1 className="t-hero-h">Registration <em>Fee</em></h1>
              <p className="t-hero-p">Loading registration information...</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiRefreshLine size={24} className="animate-spin" /></div>
          <p className="t-s-title">Loading Registration Fee</p>
          <p className="t-s-sub">Please wait while we load the registration information...</p>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Error / No Data State ──
  if (error || !feeData) {
    console.warn("⚠️ Rendering error/no-data state:", { error, feeData });
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiMoneyDollarCircleLine size={13} />
                Meetings21 · Registration
              </div>
              <h1 className="t-hero-h">Registration <em>Fee</em></h1>
              <p className="t-hero-p">Essential information for conference registration</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiFileTextLine size={28} /></div>
          <p className="t-s-title">No Registration Data Available</p>
          <p className="t-s-sub">{error || "Registration fee information will be available soon."}</p>
          <button className="t-s-btn" onClick={() => fetchRegistrationFee(true)}>
            <RiRefreshLine size={16} style={{ display: 'inline', marginRight: 6 }} />
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // ── Data Display ──
  console.log("✅ Rendering data for:", feeData.title);
  const activeCategories = feeData.categories?.filter(c => c.isActive).sort((a, b) => a.order - b.order) || [];
  const activeNotes = feeData.importantNotes?.filter(n => n.isActive).sort((a, b) => a.order - b.order) || [];

  const superEarlyPast = isDeadlinePast(feeData.superEarlyDeadline);
  const earlyPast = isDeadlinePast(feeData.earlyDeadline);
  const standardPast = isDeadlinePast(feeData.regularDeadline);

  const totalCategories = activeCategories.length;

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ══════════ HERO ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiMoneyDollarCircleLine size={13} />
              Meetings21 · Registration
            </div>
            <h1 className="t-hero-h">
              Registration <em>Fee</em>
            </h1>
            <p className="t-hero-p">
              Choose your preferred registration category and secure your participation
              at the premier academic conference of the year.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{totalCategories}</span>
              <span className="t-st-l">Categories</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{feeData.currency || "USD"}</span>
              <span className="t-st-l">Currency</span>
            </div>
        <div className="t-st">
  <span className="t-st-n">{feeData.version || "2026"}</span>
  <span className="t-st-l">Edition</span>
</div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px clamp(20px,4vw,64px) 80px' }}>

        {/* ── Refresh & Last Updated ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <span className="t-last-updated">
            <RiHistoryLine size={14} style={{ display: 'inline', marginRight: 4 }} />
            Last updated: {lastUpdated || 'Just now'}
          </span>
          <button
            className="t-refresh-btn"
            onClick={() => fetchRegistrationFee(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 18px',
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: T.white,
              color: T.primary,
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = T.primary; e.currentTarget.style.color = T.white; e.currentTarget.style.borderColor = T.primary; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.white; e.currentTarget.style.color = T.primary; e.currentTarget.style.borderColor = T.border; }}
          >
            <RiRefreshLine size={16} /> Refresh Data
          </button>
        </div>

        {/* ─── SECTION 1: IMPORTANT NOTES ─── */}
        {activeNotes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="t-card"
            style={{ marginBottom: 32 }}
          >
            <div className="t-card-header">
              <div className="t-card-icon">
                <RiErrorWarningLine size={24} style={{ color: T.primary }} />
              </div>
              <h2 className="t-card-title">Important Information</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {activeNotes.map((note, idx) => {
                const severityColors = {
                  error: { bg: '#FEE2E2', color: '#EF4444', border: '#FECACA' },
                  warning: { bg: '#FEF3C7', color: '#D97706', border: '#FDE68A' },
                  info: { bg: T.softTeal, color: T.primary, border: T.border },
                  success: { bg: '#DCFCE7', color: '#16A34A', border: '#BBF7D0' }
                };
                const colors = severityColors[note.severity] || severityColors.info;
                return (
                  <div
                    key={idx}
                    style={{
                      padding: '14px 20px',
                      background: colors.bg,
                      borderLeft: `4px solid ${colors.color}`,
                      borderRadius: 12,
                    }}
                  >
                    <p style={{ fontSize: 14, color: colors.color, lineHeight: 1.5 }}>{note.message}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ─── SECTION 2: REGISTRATION CATEGORIES & PRICING TABLE ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ marginBottom: 48 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RiPriceTag3Line size={24} style={{ color: T.primary }} />
            </div>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text }}>Registration Categories & Pricing</h2>
              <p style={{ fontSize: 14, color: T.textMuted }}>All amounts are in {feeData.currency || "USD"}</p>
            </div>
          </div>

          {activeCategories.length > 0 ? (
            <div className="t-table-wrapper">
              <table className="t-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 180 }}>Registration Category</th>
                    <th style={{ textAlign: 'center', minWidth: 180 }}>
                      Super Earlybird<br/>
                      <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>{formatDate(feeData.superEarlyDeadline)}</span>
                      {superEarlyPast && <span className="t-deadline-closed">CLOSED</span>}
                    </th>
                    <th style={{ textAlign: 'center', minWidth: 180 }}>
                      Earlybird<br/>
                      <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>{formatDate(feeData.earlyDeadline)}</span>
                      {earlyPast && <span className="t-deadline-closed">CLOSED</span>}
                    </th>
                    <th style={{ textAlign: 'center', minWidth: 180 }}>
                      Standard<br/>
                      <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>{formatDate(feeData.regularDeadline)}</span>
                      {standardPast && <span className="t-deadline-closed">CLOSED</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeCategories.map((cat, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 600, color: T.text }}>
                        {cat.name}
                        {cat.benefits && cat.benefits.length > 0 && (
                          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                            {cat.benefits.slice(0, 2).map((b, i) => (
                              <span key={i}>✓ {b} </span>
                            ))}
                            {cat.benefits.length > 2 && <span>+{cat.benefits.length - 2} more</span>}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {superEarlyPast ? (
                          <span className="t-price-closed">${cat.superEarlyPrice || '-'}</span>
                        ) : (
                          <span className="t-price">${cat.superEarlyPrice || '-'}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {earlyPast ? (
                          <span className="t-price-closed">${cat.earlyPrice || '-'}</span>
                        ) : (
                          <span className="t-price">${cat.earlyPrice || '-'}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {standardPast ? (
                          <span className="t-price-closed">${cat.regularPrice || '-'}</span>
                        ) : (
                          <span className="t-price">${cat.regularPrice || '-'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, background: T.white, borderRadius: 16, border: `1px solid ${T.borderLight}` }}>
              <p style={{ color: T.textMuted, fontSize: 14 }}>No registration categories have been added yet.</p>
              <p style={{ color: T.textMuted, fontSize: 12, marginTop: 4 }}>Check back later for updated pricing.</p>
            </div>
          )}

          <p style={{ fontSize: 12, color: T.textMuted, marginTop: 16, textAlign: 'center' }}>
            * All fees are inclusive of applicable taxes. Accommodation not included unless specified.
          </p>
        </motion.div>

        {/* ─── SECTION 3: REGISTRATION CATEGORIES EXPLAINED ─── */}
        {activeCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: 48 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RiVerifiedBadgeLine size={24} style={{ color: T.primary }} />
              </div>
              <div>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text }}>Registration Categories Explained</h2>
                <p style={{ fontSize: 14, color: T.textMuted }}>What's included in each registration category</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {activeCategories.map((cat, idx) => (
                <div key={idx} className="t-card" style={{ padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 16, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <RiPriceTag3Line size={22} style={{ color: T.primary }} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text }}>{cat.name}</h3>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: T.textMuted }}>Super Early: ${cat.superEarlyPrice}</span>
                        <span style={{ fontSize: 12, color: T.textMuted }}>Early: ${cat.earlyPrice}</span>
                        <span style={{ fontSize: 12, color: T.textMuted }}>Regular: ${cat.regularPrice}</span>
                      </div>
                    </div>
                  </div>
                  {cat.benefits && cat.benefits.length > 0 && (
                    <div style={{ borderTop: `1px solid ${T.borderLight}`, paddingTop: 16 }}>
                      {cat.benefits.map((benefit, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <RiCheckboxCircleFill size={14} style={{ color: '#16A34A' }} />
                          <span style={{ fontSize: 13, color: T.textMid }}>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ─── SECTION 4: CTA - REGISTER NOW ─── */}
        <div className="t-cta-w" style={{ padding: 0 }}>
          <motion.div
            className="t-cta"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <div>
              <div className="t-cta-tag">Secure Your Seat</div>
              <h2 className="t-cta-h">Ready to Register?<br />Join the Premier Academic Event</h2>
              <p className="t-cta-p">Complete your registration online and reserve your participation. Join leading researchers and scholars from around the world.</p>
            </div>
            <div className="t-cta-r">
              <Link
                to={eventId ? `/register?eventId=${eventId}` : "/register"}
                className="t-cta-btn"
              >
                Register Now <RiArrowRightLine size={15} />
              </Link>
              <Link to={`/contact${eventId ? `?eventId=${eventId}` : ''}`} className="t-cta-btn-outline">
                Contact Secretary
                <RiArrowRightLine size={15} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFeePage;